import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import { DialogType } from '../../common/enums.js';
import { useAuthContextMeta, useMeta } from '../../common/hooks/useMeta.js';
import { useNavigateBack } from '../../common/hooks/useNavigateBack.js';
import {
  backMsg,
  closeMsg,
  externalAuthenticationErrorMsg,
  externalAuthenticationRetryMsg,
  t,
} from '../../common/i18n/index.js';
import { isStrictFeatureEnabled } from '../../common/utils/featureFlagUtils.js';
import { DialogWrapper } from '../../components/DialogWrapper/index.js';
import { ElisaIdMigrationFlow } from '../../components/ElisaIdMigration/ElisaIdMigrationFlow.js';
import { ElisaIdMigrationModal } from '../../components/ElisaIdMigration/ElisaIdMigrationModal.js';
import { Header } from '../../components/Header/Header.js';
import { OmaElisaHeader } from '../../components/OmaElisaHeader/OmaElisaHeader.js';
import { PunchoutHeader } from '../../components/Punchout/partials/PunchoutHeader.js';
import { SelfService } from '../../components/SelfService/SelfService.js';
import { SelfServiceLogin } from '../../components/SelfServiceLoginScene/SelfServiceLogin.js';
import { SmallHeader } from '../../components/SmallHeader/SmallHeader.js';
import { IdentityProvider } from '../../generated/api/identityProvider.js';
import { getSwitchToAccountName, isMigratedContentHidden } from '../../selfservice/common/localStorageUtils.js';
import { useAuth } from './AuthProvider.js';
import { useFeatureFlags } from './ConfigProvider.js';
import { useDialog } from './DialogProvider.js';
import { closeDialog } from './dialogStore.js';
import { Spinner, SpinnerWithSwitchAccountInfoText } from './siteUtils.js';
import type { DialogData } from './dialogStore.js';
import type { JSX } from 'react';

export const PublicPathLayout = () => {
  const { isMaintenanceModeForCmsPages } = useFeatureFlags();
  if (isStrictFeatureEnabled(isMaintenanceModeForCmsPages) && isMigratedContentHidden()) {
    throw new Response('', { status: 503 });
  }

  return <Outlet />;
};
export const PrivateAuthenticationLayout = () => {
  useAuthContextMeta();
  return <Outlet />;
};
export const PublicAuthenticationLayout = () => {
  useMeta();
  return <Outlet />;
};

export const OmaElisaNavigationLayout = () => {
  const { isAuthenticated, isLoading, authenticatedUser } = useAuth();
  const switchToAccountName = getSwitchToAccountName();
  return (
    <>
      {isLoading && !switchToAccountName && <Spinner />}
      {isLoading && switchToAccountName && (
        <SpinnerWithSwitchAccountInfoText switchToAccountName={switchToAccountName} />
      )}
      {!isLoading && (isAuthenticated ? <OmaElisaHeader /> : <Header />)}
      {!isLoading &&
        (isAuthenticated ? (
          <SelfService>
            <Outlet />
          </SelfService>
        ) : (
          <Outlet />
        ))}
      {!!authenticatedUser && <ElisaIdMigrationFlow />}
    </>
  );
};

export const PunchoutNavigationLayout = () => {
  const { authenticatedUser } = useAuth();
  return (
    <>
      <PunchoutHeader hideCart={!authenticatedUser?.mobile} />
      <Outlet />
    </>
  );
};

export const OmaElisaProtectedRoutesLayout = () => {
  const { isAuthenticated, authenticatedUser, inMigrationPhase } = useAuth();
  const { inMigrationPhaseModalSkippable } = useFeatureFlags();

  if (!isAuthenticated) {
    return <SelfServiceLogin />;
  }

  const shouldShowMigrationModal = inMigrationPhase && authenticatedUser?.identityProvider === IdentityProvider.SSO;

  return (
    <>
      {shouldShowMigrationModal && (
        <ElisaIdMigrationModal
          canSkip={isStrictFeatureEnabled(inMigrationPhaseModalSkippable)}
          userEmail={authenticatedUser?.contact?.person?.userName}
        />
      )}
      <Outlet />
    </>
  );
};

// This is only for public-facing pages of portals, not for normal public pages
// (different language handling)
export const PublicHeaderLayout = () => (
  <>
    <Header />
    <Outlet />
  </>
);
export const PublicHeaderCmsLayout = () => (
  <>
    <Header cmsPage />
    <Outlet />
  </>
);

const getExtAuthDialog = (onCloseDialog: () => void, dialog?: DialogData): JSX.Element | undefined => {
  if (dialog?.params?.type === DialogType.EXTERNAL_AUTHENTICATION_FAIL) {
    return (
      <DialogWrapper
        buttons={[
          {
            id: 'close-external-authentication-error',
            onClick: onCloseDialog,
            text: t.WOYD(closeMsg),
          },
        ]}
        closeable
        header={t.UEHC(externalAuthenticationErrorMsg)}
        onCloseDialog={onCloseDialog}
      >
        <p>{t.Q9YM(externalAuthenticationRetryMsg)}</p>
      </DialogWrapper>
    );
  } else {
    return undefined;
  }
};

export const ExtAuthLayout = () => {
  const dialog = useDialog();

  return (
    <div className="of-selfservice">
      <SmallHeader />
      <Outlet />
      {getExtAuthDialog(() => closeDialog(), dialog)}
    </div>
  );
};

export const ShoppingBasketLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const loginInProgress = Boolean(location.state?.loginInProgress);
  const navigateBack = useNavigateBack(paths.WEB_SHOP);
  const clickAction = () =>
    loginInProgress
      ? () => navigate(paths.SHOPPING_BASKET, { state: { loginInProgress: false }, relative: 'path', replace: true })
      : navigateBack;

  return (
    <div className={`${dsClass.PADDING_BOTTOM_0} of-selfservice`}>
      <SmallHeader buttonText={t.D1E9(backMsg)} onClick={clickAction()} />
      <Outlet />
    </div>
  );
};

export const PlaceOrderLayout = ({
  basketPath = paths.SHOPPING_BASKET,
  thankYouPath = paths.PLACE_ORDER_THANK_YOU,
  returnToShopPath = paths.WEB_SHOP,
}: {
  basketPath?: string;
  thankYouPath?: string;
  returnToShopPath?: string;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isThankYouPage = location.pathname.endsWith(thankYouPath);

  const [formEdited, setFormEdited] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);

  const confirmNavigation = () => {
    navigate(basketPath, { state: { from: 'kassa' }, replace: true });
  };

  const cancelNavigation = () => {
    setShowReturnModal(false);
  };

  const handleBackClick = () => {
    if (isThankYouPage) {
      navigate(returnToShopPath);
    } else if (formEdited) {
      setShowReturnModal(true);
    } else {
      confirmNavigation();
    }
  };

  return (
    <div className="of-selfservice">
      <SmallHeader
        buttonText={isThankYouPage ? t.PLAW('Return to the shop') : t.DKXU('Back to cart')}
        onClick={handleBackClick}
      />
      <Outlet context={{ cancelNavigation, confirmNavigation, setFormEdited, showReturnModal }} />
    </div>
  );
};
